import { useMemo } from "react";

import {
    PRE_DEFINED_FIELD,
    TECHNOGRAPHICS_FIELD,
} from "app/pages/sales-intelligence/common-components/export-table/const";
import {
    addPreDefinedItem,
    addTechographicsItem,
} from "app/pages/sales-intelligence/common-components/export-table/constants";
import type { SimilarwebFieldConfig } from "app/pages/sales-intelligence/common-components/export-table/types";
import {
    getUniqueFieldName,
    resolveSimilarwebValueType,
} from "app/pages/sales-intelligence/common-components/export-table/utils";
import { getScoringFieldKeys } from "app/pages/sales-intelligence/common-components/export-table/types";
import { buildSimilarwebFieldDropdownOptions } from "app/pages/sales-intelligence/pages/manage-integration/configs/accountsFields";
import type {
    ExportItem,
    ExportName,
    SimilarwebFieldsConfig,
} from "app/pages/sales-intelligence/sub-modules/management/store/type";
import {
    getConfigByExportName,
    getExportSettingSourceType,
    getExportSettingTitle,
    isFieldEligibleForEnrichment,
} from "app/pages/sales-intelligence/sub-modules/management/utils/utils";

import type {
    CrmEnrichmentJobObject,
    CrmEnrichmentJobType,
    CrmProvider,
    EnrichmentJobMappingDTO,
} from "../types/types";
import { ENRICHMENT_OBJECT_TO_EXPORT_NAME } from "../utils/enrichmentObjectToExportName";
import { isPredefinedField, isTechnologiesField } from "../utils/mappingValidation";

type Params = {
    crmProvider: CrmProvider;
    enrichmentObject: CrmEnrichmentJobObject;
    jobType: CrmEnrichmentJobType | "";
    mappings: readonly EnrichmentJobMappingDTO[];
};

const ADD_FIELD_KEY = "internal_add";

const toExportItem = (
    mapping: EnrichmentJobMappingDTO,
    fieldsConfig: SimilarwebFieldsConfig,
    index: number,
    builtRows: { field: string }[],
): ExportItem => {
    const { metadata = "", sourceType } = mapping;

    let field: string;
    if (isTechnologiesField(sourceType)) {
        field = `${TECHNOGRAPHICS_FIELD}_${index}`;
    } else if (isPredefinedField(sourceType)) {
        field = `${PRE_DEFINED_FIELD}_${index}`;
    } else if (metadata) {
        field = getUniqueFieldName(metadata, builtRows).fieldName;
    } else {
        field = "";
    }

    builtRows.push({ field });

    return {
        externalField: mapping.externalField,
        overwrite: mapping.overwrite,
        sourceType,
        metadata,
        field,
        title: getExportSettingTitle(metadata, sourceType, fieldsConfig),
        example: fieldsConfig[metadata]?.example ?? "",
        valueType: resolveSimilarwebValueType({
            sourceType,
            configValueType: fieldsConfig[metadata]?.valueType,
        }),
    };
};

const useMappingTableData = ({ crmProvider, enrichmentObject, jobType, mappings }: Params) => {
    const exportName: ExportName = ENRICHMENT_OBJECT_TO_EXPORT_NAME[enrichmentObject];
    const isSalesforce = crmProvider === "salesforce";

    const fieldsConfig = useMemo(
        () => getConfigByExportName(crmProvider, exportName) as SimilarwebFieldsConfig,
        [crmProvider, exportName],
    );

    const addItem: ExportItem = useMemo(
        () => ({
            field: ADD_FIELD_KEY,
            title: "",
            externalField: "internal_addItem",
            example: "internal_add_example",
            overwrite: false,
            delete: null,
        }),
        [],
    );

    const tableData: readonly ExportItem[] = useMemo(() => {
        const builtRows: { field: string }[] = [];
        const items = mappings.map((m, i) => toExportItem(m, fieldsConfig, i, builtRows));
        return [...items, addItem];
    }, [mappings, fieldsConfig, addItem]);

    const dataLength = tableData.length;

    const availableFieldKeys = useMemo(() => {
        const scoringKeys = jobType !== "scheduled" ? new Set(getScoringFieldKeys()) : null;

        return Object.keys(fieldsConfig).filter((key) => {
            if (scoringKeys?.has(key)) {
                return false;
            }
            const sourceType = getExportSettingSourceType(key);
            return isFieldEligibleForEnrichment(sourceType);
        });
    }, [fieldsConfig, jobType]);

    const regularSimilarwebFields: SimilarwebFieldConfig[] = useMemo(() => {
        const scoringKeys = new Set<string>(getScoringFieldKeys());
        const scoring: SimilarwebFieldConfig[] = [];
        const rest: SimilarwebFieldConfig[] = [];

        for (const key of availableFieldKeys) {
            const config = fieldsConfig[key];
            if (!config) {
                continue;
            }
            if (scoringKeys.has(key)) {
                scoring.push(config);
            } else {
                rest.push(config);
            }
        }

        return [...scoring, ...rest];
    }, [availableFieldKeys, fieldsConfig]);

    const similarwebFields: SimilarwebFieldConfig[] = useMemo(
        () =>
            buildSimilarwebFieldDropdownOptions({
                fields: regularSimilarwebFields,
                predefinedItems: [addPreDefinedItem],
                techographicsItem: exportName === "contacts" && isSalesforce ? undefined : addTechographicsItem,
            }),
        [exportName, isSalesforce, regularSimilarwebFields],
    );

    const selectedCrmFields = useMemo(() => tableData.map((item) => item.externalField), [tableData]);

    return {
        exportName,
        tableData,
        dataLength,
        similarwebFields,
        selectedCrmFields,
    };
};

export default useMappingTableData;
